import { useState } from 'react';
import { NJButton, NJCheckbox, NJText } from '@engie-group/fluid-design-system-react';
import { UMFormStyles, UMSelect, UMR } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { exportPlan } from './construction';
import { downloadText, exportPhases } from './phaseFiles';
import { PhaseExchange } from './PhaseExchange';
import type { Plan, Project } from './types';

export function ProjectExchange({ project, geo, initialPlanId, canWrite, onUpdate, onSST, draft, onDraft }: {
  project: Project; geo?: PlanGeoData; initialPlanId: string; canWrite: boolean;
  onUpdate: (id: string, changes: Partial<Plan>) => void; onSST: () => void;
  draft: Plan | null; onDraft: (plan: Plan | null) => void;
}) {
  const [selected, setSelected] = useState<string[]>(initialPlanId ? [initialPlanId] : []);
  const [format, setFormat] = useState('json');
  const [importId, setImportId] = useState(draft?.id || initialPlanId || project.plans[0]?.id || '');
  const [message, setMessage] = useState('');
  const plan = project.plans.find((p) => p.id === importId);
  const selectedPlans = project.plans.filter((p) => selected.includes(p.id));
  const disabled = !selectedPlans.length || !geo || selectedPlans.some((p) => p.status === 'running') || !!draft;
  return <section style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
    <UMFormStyles />
    <NJText as="h2" scale="lg">Import-export du projet</NJText>
    <NJText scale="sm">Chaque export télécharge le récapitulatif complet : paramètres projet, six jeux et versions, composition, SST projet et archives exactes séparées du courant.
      Les formats CSV / GeoJSON téléchargent en plus leurs fichiers d’échange. Autorisez les téléchargements multiples dans votre navigateur.</NJText>
    <NJText as="h3" scale="md">Plans à exporter</NJText>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {project.plans.map((p) => <NJCheckbox key={p.id} label={p.name} isChecked={selected.includes(p.id)}
        onChange={(_, checked) => setSelected(checked ? [...new Set([...selected, p.id])] : selected.filter((id) => id !== p.id))} />)}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
      <UMSelect value={format} ariaLabel="Format export projet" onChange={setFormat} options={[
        { value: 'json', label: 'Dossier complet · JSON' },
        { value: 'csv', label: 'Récap complet + phasage · CSV Excel' },
        { value: 'sst', label: 'Récap complet + SST projet · GeoJSON' },
      ]} />
      <NJButton label="Télécharger l’export projet" disabled={disabled} onClick={() => {
        try {
          const dossier = {
            format: 'plan2cost-projet-v2', exportedAt: new Date().toISOString(),
            project: { id: project.id, name: project.name, params: project.params, technicalPoints: project.technicalPoints },
            plans: selectedPlans.map((p) => exportPlan(project, p, geo)),
            // Copie incluse pour ne pas perdre l'échange si un second téléchargement est bloqué.
            exchangeFiles: format === 'csv' ? selectedPlans.map((p) => ({
              name: `${p.id}-phasage.csv`, content: exportPhases(p, geo?.pipes.features.length ?? 0),
            })) : [],
          };
          downloadText(`${project.id}-recapitulatif-complet.json`, JSON.stringify(dossier, null, 2), 'application/json');
          if (format === 'csv') selectedPlans.forEach((p) =>
            downloadText(`${p.id}-phasage.csv`, exportPhases(p, geo?.pipes.features.length ?? 0), 'text/csv;charset=utf-8'));
          if (format === 'sst') downloadText(`${project.id}-sst.geojson`, JSON.stringify({
            type: 'FeatureCollection', recapitulatif: dossier,
            features: (project.technicalPoints ?? []).map(({ coordinates, ...properties }) => ({
              type: 'Feature', properties, geometry: { type: 'Point', coordinates },
            })),
          }, null, 2), 'application/geo+json');
          setMessage(`${selectedPlans.length} plan(s) exporté(s), récapitulatif complet inclus. Archives absentes signalées, jamais reconstruites.`);
        } catch (error) { setMessage(`Export impossible : ${error instanceof Error ? error.message : String(error)}`); }
      }} />
    </div>
    {!selected.length && <NJText role="status" scale="sm">Sélectionnez au moins un plan pour télécharger.</NJText>}
    {message && <NJText role="status">{message}</NJText>}
    <section style={{ marginTop: 24, borderTop: `1px solid ${UMR.border}` }}>
      <NJText as="h3" scale="md">Imports locaux</NJText>
      {canWrite ? <>
        <NJButton label="Importer / vérifier des SST projet" variant="secondary" scale="sm" onClick={onSST} />
        <NJText scale="xs">Le GeoJSON est vérifié dans les paramètres SST du projet : aperçu des points, conflits et confirmation de remplacement.</NJText>
        <fieldset disabled={!!draft} style={{ border: 0, padding: 0 }}>
          <UMSelect value={importId} ariaLabel="Plan destinataire du CSV" onChange={setImportId}
            options={project.plans.map((p) => ({ value: p.id, label: p.name }))} />
        </fieldset>
        {plan && <PhaseExchange key={plan.id} plan={draft ?? plan} pipeCount={geo?.pipes.features.length ?? 0}
          canWrite={canWrite && plan.status !== 'running'} onUpdate={(changes) => onDraft({ ...(draft ?? plan), ...changes })} />}
        {draft && <div style={{ display: 'flex', gap: 8 }}>
          <NJButton label="Confirmer les dates importées" onClick={() => { onUpdate(draft.id, {
            buildingPhases: draft.buildingPhases, pipePhaseOverrides: draft.pipePhaseOverrides,
          }); onDraft(null); setMessage('Dates confirmées ; agrégats annuels mis à jour.'); }} />
          <NJButton label="Annuler les dates importées" variant="secondary" onClick={() => onDraft(null)} />
        </div>}
      </> : <NJText scale="sm">Lecture seule : exports autorisés ; imports et modifications interdits.</NJText>}
    </section>
  </section>;
}
